import React, { Suspense, useState } from "react";
import { createBrowserRouter } from "react-router-dom";
import { RouterProvider } from "react-router-dom";
import { DynamicContextProvider } from "@dynamic-labs/sdk-react-core";
import { EthereumWalletConnectors } from "@dynamic-labs/ethereum";
import { DynamicWagmiConnector } from "@dynamic-labs/wagmi-connector";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { WagmiProvider } from "wagmi";

import { routes } from "@/router/routes";
import SuspenseFallback from "@/components/layout/SuspenseFallback";
import { handleAuthSuccess, handleAuthLogout } from "./helpers";
import { useWalletInstance } from "./contexts/WalletContext";
import { useToast } from "./components/ui/use-toast";
import { siweMessage } from "@dripit/constants";
import { isProduction, evmNetworks, config } from "@/helpers";

const router = createBrowserRouter(routes);

const ContainerComponent: React.FC = () => {
  const { setWalletInstance } = useWalletInstance();
  const { toast } = useToast();
  const [authSignature, setAuthSignature] = useState<string | null>(null);
  const [sigMessage, setSigMessage] = useState<string | null>(null);

  return (
    <DynamicContextProvider
      settings={{
        environmentId: isProduction
          ? "fc825a2e-c3e5-4758-87d9-0e8267817c97"
          : "d7677a9c-96be-4034-bc77-6ed585651435", // todo: move to .env
        siweStatement: siweMessage,
        overrides: { evmNetworks },
        recommendedWallets: [
          { walletKey: "core", label: "Recommended" },
          { walletKey: "rabby", label: "Popular" },
          { walletKey: "metamask", label: "Popular" },
          { walletKey: "phantomevm", label: "Popular" },
        ],
        walletConnectors: [EthereumWalletConnectors],
        events: {
          onAuthSuccess: async (data) =>
            await handleAuthSuccess(
              { ...data, sigMessage, authSignature },
              { toast, setWalletInstance }
            ),
          onLogout: () => {
            handleAuthLogout();
          },
          onSignedMessage: ({ messageToSign, signedMessage }) => {
            if (messageToSign.includes(siweMessage)) {
              setAuthSignature(signedMessage);
              setSigMessage(messageToSign);
            }
          },
        },
      }}>
      <WagmiProvider config={config}>
        <Suspense fallback={<SuspenseFallback />}>
          <DynamicWagmiConnector>
            <RouterProvider router={router} />
          </DynamicWagmiConnector>
        </Suspense>
      </WagmiProvider>
      {import.meta.env.VITE_HIDE_QUERY_TOOLS !== "true" && (
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
      )}
    </DynamicContextProvider>
  );
};

export default React.memo(ContainerComponent);
